import * as Yup from 'yup';

import useComponentStore from '@/stores/components';
import {
  MESSAGE_TYPES,
  EMPHASIS_TYPES,
  BUTTON_TYPES,
} from '@/config/constant';

const EMAIL_REGEX = /^[-!#$%&'*+\\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
const PASSWORD_REGEX = /^(?!\s+)(?!.*\s+$)(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[$^*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ])[A-Za-z0-9$^*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]{8,100}$/;
const DESCRIPTION_FORMAT_REGEX = /^(?:\s*(USD|\$|US\$|KRW|₩|원)\s*)?[0-9,.]{1,14}(?:\s*(USD|\$|US\$|KRW|₩|원)\s*)?$/;
const CURRENCY_SYMBOL_REGEX = /USD|\$|US\$|KRW|₩|원/g;

const containsVariable = (value) => {
  const text = value || '';

  return text.match(/#\{([\w.-]+)}/g);
};

// OP-166: 링크 전체가 변수(#{URL} 등)로 시작하는지 — 이 경우만 URL 형식 검사를 건너뜀
const startsWithVariable = (value) => /^#\{[\w.-]+}/.test(value || '');

Yup.addMethod(Yup.number, 'positiveNumeric', function positiveNumeric(message) {
  return this.test({
    message,
    name: 'positiveNumeric',
    exclusive: true,
    test(value) {
      return value === null || (value || 0) >= this.resolve(0);
    },
  });
});

Yup.addMethod(Yup.number, 'positiveInteger', function positiveInteger(message) {
  return this.test({
    message,
    name: 'positiveInteger',
    exclusive: true,
    test(value) {
      if (!value) {
        return true;
      }

      if (!Number.isInteger(value)) {
        return false;
      }

      return (value || 0) >= this.resolve(0);
    },
  });
});

Yup.addMethod(Yup.string, 'nospace', function nospace(message) {
  return this.test({
    message,
    name: 'nospace',
    exclusive: true,
    test(value = '') {
      return !/\s/.exec(value);
    },
  });
});

Yup.addMethod(Yup.string, 'laxUrl', function laxUrl(message) {
  return this.test({
    message,
    name: 'laxUrl',
    exclusive: true,
    test(value = '') {
      if (!value) {
        return true;
      }

      return !!/^(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/i.test(value);
    },
  });
});

Yup.addMethod(Yup.string, 'password', function password(message) {
  return this.test({
    message,
    name: 'password',
    exclusive: true,
    test(value = '') {
      if (!value) {
        return true;
      }

      return PASSWORD_REGEX.test(value);
    },
  });
});

Yup.addMethod(Yup.string, 'newPassword', function newPassword({ currentPassword, confirmNewPassword }) {
  return this.test({
    message: () => ('newPassword'),
    name: 'newPassword',
    exclusive: true,
    test(value, context) {
      if (value === currentPassword) {
        return context.createError({ message: 'newPassword.same' });
      }

      if (value !== confirmNewPassword) {
        return context.createError({ message: 'newPassword.mismatch' });
      }

      return PASSWORD_REGEX.test(value);
    },
  });
});

Yup.addMethod(Yup.string, 'customEmail', function customEmail(message) {
  return this.test({
    message,
    name: 'customEmail',
    exclusive: true,
    test(value = '') {
      if (!value) {
        return true;
      }

      const emailParts = value.split('@');
      if (emailParts.length !== 2) {
        return false;
      }

      const [account, address] = emailParts;
      if (account.length > 64) {
        return false;
      }

      if (address.length > 255) {
        return false;
      }

      const domainParts = address.split('.');
      if (domainParts.some((part) => part.length > 63)) {
        return false;
      }

      return EMAIL_REGEX.test(value);
    },
  });
});

Yup.addMethod(Yup.mixed, 'checkAddChannelButton', function checkAddChannelButton(item, message) {
  return this.test({
    message,
    name: 'checkAddChannelButton',
    test() {
      return item.type === 'AC';
    },
  });
});

Yup.addMethod(Yup.string, 'messageType', function messageType(message) {
  return this.test({
    message,
    name: 'messageType',
    test(value) {
      return MESSAGE_TYPES.includes(value);
    },
  });
});

Yup.addMethod(Yup.string, 'emphasizeType', function emphasizeType(message) {
  return this.test({
    message,
    name: 'emphasizeType',
    test(value) {
      return EMPHASIS_TYPES.includes(value);
    },
  });
});

Yup.addMethod(Yup.string, 'buttonType', function buttonType(message) {
  return this.test({
    message,
    name: 'buttonType',
    test(value) {
      // OP-166: 카카오 비즈니스폼(BF) 버튼 지원 종료 — 신규·수정 저장 모두 차단
      if (value === 'BF') {
        return this.createError({ message: 'button.businessFormEnded' });
      }

      return BUTTON_TYPES.includes(value);
    },
  });
});

Yup.addMethod(Yup.string, 'recipientNumber', function recipientNumber(message) {
  return this.test({
    message,
    name: 'recipientNumber',
    test(value) {
      if (!value) {
        return true;
      }

      return /^(\+?82|0|\+?820)10\d{8}$/.test(value);
    },
  });
});

Yup.addMethod(Yup.string, 'allowExtension', function allowExtension(message) {
  return this.test({
    message,
    name: 'allowExtension',
    test: (extension) => [
      'jpg',
      'jpeg',
      'png',
    ].includes(extension),
  });
});

Yup.addMethod(Yup.string, 'allowExtensionJpg', function allowExtensionJpg(message) {
  return this.test({
    message,
    name: 'allowExtensionJpg',
    test: (extension) => [
      'jpg',
      'jpeg',
    ].includes(extension),
  });
});

Yup.addMethod(Yup.string, 'allowInquiryFileExtension', function allowInquiryFileExtension(message) {
  return this.test({
    message,
    name: 'allowInquiryFileExtension',
    test: (extension) => [
      'png',
      'jpg',
      'jpeg',
      'gif',
      'pdf',
      'hwp',
      'doc',
      'docx',
    ].includes(extension),
  });
});

Yup.addMethod(Yup.string, 'allowHeader', function allowHeader(message) {
  return this.test({
    message,
    name: 'allowHeader',
    test: (header) => [
      'ffd8ffe0',
      'ffd8ffe1',
      '89504e47',
    ].includes(header),
  });
});

Yup.addMethod(Yup.string, 'uuid', function uuid(message) {
  return this.test({
    name: 'uuid',
    exclusive: true,
    message,
    test(value = '') {
      if (!value) {
        return true;
      }

      return /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i.test(value);
    },
  });
});

Yup.addMethod(Yup.object, 'securityPolicy', function securityPolicy(message) {
  return this.test({
    name: 'securityPolicy',
    message,
    test(value) {
      return value.iprestrictEnabled || value.mfaEnabled;
    },
  });
});

Yup.addMethod(Yup.string, 'loginId', function loginId(message) {
  return this.test({
    message,
    name: 'loginId',
    test(value) {
      if (!value) {
        return true;
      }

      return !EMAIL_REGEX.test(value);
    },
  });
});

Yup.addMethod(Yup.object, 'newPassword', function password(item) {
  return this.test({
    name: 'newPassword',
    test(_, context) {
      const {
        currentPassword,
        newPassword,
        confirmNewPassword,
      } = item;

      if (!newPassword) {
        return true;
      }

      if (newPassword === currentPassword) {
        return context.createError({ message: 'newPassword.same' });
      }

      if (newPassword !== confirmNewPassword) {
        return context.createError({ message: 'newPassword.mismatch' });
      }

      if (!PASSWORD_REGEX.test(newPassword)) {
        return context.createError({ message: 'password.format' });
      }

      return true;
    },
  });
});

Yup.addMethod(Yup.string, 'disallowedVariable', function disallowedVariable(message) {
  return this.test({
    message,
    name: 'disallowedVariable',
    test(value) {
      return !containsVariable(value);
    },
  });
});

Yup.addMethod(Yup.string, 'descriptionFormat', function descriptionFormat(message) {
  return this.test({
    message,
    name: 'descriptionFormat',
    test(value, context) {
      if (containsVariable(value)) {
        return true;
      }

      if (!DESCRIPTION_FORMAT_REGEX.test(value)) {
        return context.createError({ message });
      }

      if (value.match(CURRENCY_SYMBOL_REGEX)?.length > 1) {
        return context.createError({ message });
      }

      if (value.replace(/[^0-9.]/g, '').match(/[.]/g)?.length > 1) {
        return context.createError({ message });
      }

      return true;
    },
  });
});

const password = Yup
  .string()
  .required('password.required')
  .password('password.format');

const loginId = Yup
  .string()
  .required('loginId.required')
  .max(100, 'loginId.max')
  .loginId('loginId.format');

const buttonValidateMap = {
  // delivery
  DS: () => ({
    type: Yup.string().buttonType('button.type'),
    name: Yup
      .string()
      .required('button.name.required')
      .max(14, 'button.name.max')
      .disallowedVariable('button.name.disallowedVariable'),
  }),
  // web link
  WL: () => ({
    type: Yup.string().buttonType('button.type'),
    name: Yup
      .string()
      .required('button.name.required')
      .max(14, 'button.name.max')
      .disallowedVariable('button.name.disallowedVariable'),
    linkMo: Yup
      .string()
      .required('button.mobileUrl.required')
      .max(500, 'button.mobileUrl.max'),
    linkPc: Yup
      .string()
      .max(500, 'button.pcUrl.max'),
  }),
  // app link
  AL: () => ({
    type: Yup.string().buttonType('button.type'),
    name: Yup
      .string()
      .required('button.name.required')
      .max(14, 'button.name.max')
      .disallowedVariable('button.name.disallowedVariable'),
    schemeAndroid: Yup
      .string()
      .required('button.androidDeepLink.required')
      .max(500, 'button.androidDeepLink.max'),
    schemeIos: Yup
      .string()
      .required('button.iosDeepLink.required')
      .max(500, 'button.iosDeepLink.max'),
  }),
  // message delivery
  MD: () => ({
    type: Yup.string().buttonType('button.type'),
    name: Yup
      .string()
      .required('button.name.required')
      .max(14, 'button.name.max')
      .disallowedVariable('button.name.disallowedVariable'),
  }),
  // add channel
  AC: () => ({
    type: Yup.string().buttonType('button.type'),
    name: Yup
      .string()
      .max(14, 'button.name.max')
      .disallowedVariable('button.name.disallowedVariable'),
  }),
  BF: () => ({
    type: Yup.string().buttonType('button.type'),
    name: Yup
      .string()
      .oneOf([
        '톡에서 설문하기',
        '톡에서 응모하기',
        '톡에서 예약하기',
      ], 'button.businessFormName'),
    bizFormId: Yup
      .string()
      .required('button.bizFormKey.required'),
  }),
};

const validateMap = {
  findId: () => ({
    email: Yup
      .string()
      .required('email.required')
      .customEmail('email.format'),
  }),
  findPassword: () => ({
    id: loginId,
    email: Yup
      .string()
      .required('email.required')
      .customEmail('email.format'),
  }),
  login: () => ({
    id: loginId,
    password,
  }),
  department: () => ({
    name: Yup
      .string()
      .required('department.name.required')
      .max(50, 'department.name.max'),
    code: Yup
      .string()
      .required('department.code.required')
      .max(50, 'department.code.max'),
  }),
  changePassword: (item) => ({
    currentPassword: password,
    newPassword: Yup
      .string()
      .required('newPassword.required')
      .password('password.format'),
    confirmNewPassword: Yup
      .string()
      .required('confirmNewPassword.required')
      .password('password.format'),
    passwordCheck: Yup.object().newPassword(item),
  }),
  // mfa: () => ({
  //   selectedValue: Yup.string().required(''),
  //   code: Yup.string().required('mfa.code.required'),
  // }),
  alimtalkTemplate: (item) => {
    const result = {
      // 발신 프로필
      kakaoProfileId: Yup
        .string()
        .required('kakaoProfile.required'),
      // 템플릿 명
      name: Yup
        .string()
        .required('templateName.required')
        .max(200, 'templateName.max'),
      // 템플릿 내용
      content: Yup
        .string()
        .required('content.required')
        .max(1000, 'content.max'),
      // 버튼
      buttons: item.buttons
        .map(({ type, linkMo, linkPc }) => {
          const map = (buttonValidateMap[type] ?? buttonValidateMap.MD)();

          // OP-166: 변수로 시작하는 링크(#{URL})만 예외, 그 외는 http(s):// 시작 필수 (앱스킴 차단)
          if (map.linkMo && linkMo && !startsWithVariable(linkMo)) {
            map.linkMo = map.linkMo.laxUrl('button.mobileUrl.url');
          }

          if (map.linkPc && linkPc && !startsWithVariable(linkPc)) {
            map.linkPc = map.linkPc.laxUrl('button.pcUrl.url');
          }

          return map;
        }),
      buttonListSize: Yup.number().max(5, 'button.size'),
      // 메시지 유형
      messageType: Yup.string().messageType('messageType'),
      // 강조 유형
      emphasizeType: Yup.string().emphasizeType('emphasizeType'),
    };

    const temp = item;
    temp.buttonListSize = item.buttons.length;
    const mainContents = { maxLength: 1000, message: ['EX.mainContents'] };

    const emphasizeTypeMap = {
      NONE() {},
      TEXT() {
        result.title = Yup
          .string()
          .max(50, 'TEXT.mainTitle.max')
          .required('TEXT.mainTitle.required');

        result.subTitle = Yup
          .string()
          .max(50, 'TEXT.subTitle.max')
          .disallowedVariable('TEXT.subTitle.disallowedVariable')
          .required('TEXT.subTitle.required');
      },
      IMAGE() {
        result.imageId = Yup
          .string()
          .required('IMAGE.image.required')
          .uuid('IMAGE.image.uuid');
      },
      ITEM_LIST() {
        // 아이템리스트 포함 시, 템플릿 내용/부가정보 합 최대 700자
        mainContents.maxLength -= 300;
        mainContents.message.push('itemList');

        result.itemList = {};

        result.itemList.list = item.itemList.list.map(() => {
          const elm = {
            title: Yup
              .string()
              .required('itemList.title.required')
              .max(6, 'itemList.title.max')
              .disallowedVariable('itemList.title.disallowedVariable'),
            description: Yup
              .string()
              .required('itemList.description.required')
              .max(23, 'itemList.description.max'),
          };

          return elm;
        });

        temp.size = item.itemList.list.length;

        result.size = Yup
          .number()
          .min(2, 'itemList.size.min')
          .max(10, 'itemList.size.max');

        // 아이템 하이라이트
        if (item.itemHighlight?.enabled) {
          result.itemHighlight = {};

          result.itemHighlight.title = Yup
            .string()
            .required('itemList.highlight.title.required')
            .max(30, 'itemList.highlight.title.max');
          // .disallowedVariable('itemList.highlight.title.disallowedVariable');

          result.itemHighlight.description = Yup
            .string()
            .required('itemList.highlight.description.required')
            .max(19, 'itemList.highlight.description.max');

          // 하이라이트 이미지
          if (item.itemHighlight?.imageEnabled) {
            result.itemHighlight.imageId = Yup
              .string()
              .required('itemList.highlight.image.required')
              .uuid('itemList.highlight.image.uuid');

            result.itemHighlight.title = result
              .itemHighlight
              .title
              .max(21, 'itemList.highlight.title.max');

            result.itemHighlight.description = result
              .itemHighlight
              .description
              .max(13, 'itemList.highlight.description.max');
          }
        }

        // 요약 정보
        if (item.itemList?.summary?.enabled) {
          result.itemList.summary = {
            title: Yup
              .string()
              .required('itemList.summary.title.required')
              .max(6, 'itemList.summary.title.max')
              .disallowedVariable('itemList.summary.title.disallowedVariable'),
            description: Yup
              .string()
              .required('itemList.summary.description.required')
              .descriptionFormat('itemList.summary.description.format')
              .max(14, 'itemList.summary.description.max'),
          };
        }

        if (item?.imageEnabled) {
          result.imageId = Yup
            .string()
            .required('itemList.image.required')
            .uuid('itemList.image.uuid');
        }

        if (item?.headerEnabled) {
          result.header = Yup
            .string()
            .required('itemList.header.required')
            .max(16, 'itemList.header.max');
        }
      },
    };

    (emphasizeTypeMap[item.emphasizeType] ?? emphasizeTypeMap.NONE)();

    const messageTypeMap = {
      ALIMTALK_BA() {},
      ALIMTALK_EX() {
        result.extra = Yup
          .string()
          .required('EX.extra.required')
          .disallowedVariable('EX.extra.disallowedVariable')
          .max(500, 'EX.extra.max');
      },
      ALIMTALK_AD() {
        // 안내 멘트는 최대 80자
        mainContents.maxLength -= 80;
        mainContents.message.push('addChannel');

        result.checkAddChannelButton = Yup
          .mixed()
          .checkAddChannelButton(item.buttons[0], 'AD.addChannel');
      },
      ALIMTALK_MI() {
        // 안내 멘트는 최대 80자
        mainContents.maxLength -= 80;
        mainContents.message.push('addChannel');

        result.extra = Yup
          .string()
          .required('MI.extra.required')
          .disallowedVariable('MI.extra.disallowedVariable')
          .max(500, 'MI.extra.max');

        result.checkAddChannelButton = Yup
          .mixed()
          .checkAddChannelButton(item.buttons[0], 'MI.addChannel');
      },
    };

    // 아이템리스트 포함 시, 템플릿 내용/부가정보 합 최대 700자
    // 안내 멘트는 최대 80자로 변수 사용과 url 포함이 불가능합니다. (본문과 합쳐 총 1,000자를 넘을 수 없음, 부가정보형과 동시 사용 가능)
    temp.mainContentsLength = (item.content?.length || 0) + (item.extra?.length || 0);
    result.mainContentsLength = Yup
      .number()
      .max(mainContents.maxLength, `${mainContents.message.join('.')}.max`);

    (messageTypeMap[item.messageType] ?? messageTypeMap.ALIMTALK_BA)();

    return result;
  },
  administrator: (item) => {
    const result = {
      name: Yup
        .string()
        .required('administrator.name.required')
        .max(100, 'administrator.name.max'),
      phoneNumber: Yup.string(),
      loginId,
      email: Yup
        .string()
        .required('administrator.email.required')
        .customEmail('administrator.email.customEmail'),
      securityPolicy: Yup.object().securityPolicy('administrator.securityPolicy'),
      ipList: item.ipList.map(() => ({
        // ip: Yup.string().required().cidr(),
        ip: Yup
          .string()
          .required('administrator.ip.required')
          .max(50, 'administrator.ip.max'),
        comment: Yup
          .string()
          .max(100, 'administrator.comment.max'),
      })),
    };

    return result;
  },
  imageType: (item) => {
    const map = {
      'textmessage-MMS_IMAGE': {
        size: Yup.number().max(500 * 1024, 'image.mms.size'),
        extension: Yup.string().allowExtensionJpg('image.mms.extension'),
      },
      'friendtalk-NORMAL_IMAGE': {
        size: Yup.number().max(500 * 1024, 'image.normal.size'),
        extension: Yup.string().allowExtension('image.normal.extension'),
        width: Yup
          .number()
          .min(500, 'image.normal.width.min')
          .max(4500, 'image.normal.width.max'),
        height: Yup
          .number()
          .min(250, 'image.normal.height.max')
          .max(4500, 'image.normal.height.min'),
        ratio: Yup.number()
          .min(0.5, 'image.normal.ratio.min')
          .max(4 / 3, 'image.normal.ratio.max'),
      },
      'friendtalk-WIDE_IMAGE': {
        size: Yup.number().max(5 * 1024 * 1024, 'image.wide.size'),
        extension: Yup.string().allowExtension('image.wide.extension'),
        width: Yup
          .number()
          .min(800, 'image.wide.width.min')
          .max(800, 'image.wide.width.max'),
        height: Yup
          .number()
          .min(600, 'image.wide.height.min')
          .max(600, 'image.wide.height.max'),
      },
      // 브랜드 메세지 스펙: 1번째 아이템은 메인(2:1), 2~4번째 아이템은 일반(1:1) 이미지
      'friendtalk-MAIN_WIDE_ITEM_LIST_IMAGE': {
        size: Yup.number().max(5 * 1024 * 1024, 'image.mainWideItemList.size'),
        extension: Yup.string().allowExtension('image.mainWideItemList.extension'),
        width: Yup
          .number()
          .min(500, 'image.mainWideItemList.width.min')
          .max(4500, 'image.mainWideItemList.width.max'),
        ratio: Yup.number()
          .oneOf([0.5], 'image.mainWideItemList.ratio'),
      },
      'friendtalk-WIDE_ITEM_LIST_IMAGE': {
        size: Yup.number().max(5 * 1024 * 1024, 'image.wideItemList.size'),
        extension: Yup.string().allowExtension('image.wideItemList.extension'),
        width: Yup
          .number()
          .min(500, 'image.wideItemList.width.min')
          .max(4500, 'image.wideItemList.width.max'),
        ratio: Yup.number()
          .oneOf([1], 'image.wideItemList.ratio'),
      },
      'friendtalk-CAROUSEL_FEED_IMAGE': {
        size: Yup.number().max(5 * 1024 * 1024, 'image.carouselFeed.size'),
        extension: Yup.string().allowExtension('image.carouselFeed.extension'),
        width: Yup
          .number()
          .min(500, 'image.carouselFeed.width.min')
          .max(4500, 'image.carouselFeed.width.max'),
        height: Yup
          .number()
          .max(4500, 'image.carouselFeed.height.max'),
        ratio: Yup.number()
          .min(0.5, 'image.carouselFeed.ratio.min')
          .max(4 / 3, 'image.carouselFeed.ratio.max'),
      },
      'friendtalk-CAROUSEL_COMMERCE_IMAGE': {
        size: Yup.number().max(5 * 1024 * 1024, 'image.carouselCommerce.size'),
        extension: Yup.string().allowExtension('image.carouselCommerce.extension'),
        width: Yup
          .number()
          .min(500, 'image.carouselCommerce.width.min')
          .max(4500, 'image.carouselCommerce.width.max'),
        height: Yup
          .number()
          .max(4500, 'image.carouselCommerce.height.max'),
        ratio: Yup.number()
          .min(0.5, 'image.carouselCommerce.ratio.min')
          .max(4 / 3, 'image.carouselCommerce.ratio.max'),
      },
      'alimtalk-ALIMTALK_IMAGE': {
        size: Yup.number().max(500 * 1024, 'image.alimtalk.size'),
        extension: Yup.string().allowExtension('image.alimtalk.extension'),
        width: Yup
          .number()
          .min(500, 'image.alimtalk.width.min'),
        height: Yup
          .number()
          .min(250, 'image.alimtalk.height.min'),
        ratio: Yup.number()
          .min(0.5, 'image.alimtalk.ratio.min')
          .max(0.5, 'image.alimtalk.ratio.max'),
      },
      'alimtalk-ALIMTALK_ITEM_HIGHLIGHT_IMAGE': {
        size: Yup.number().max(500 * 1024, 'image.alimtalkItemHighlight.size'),
        extension: Yup.string().allowExtension('image.alimtalkItemHighlight.extension'),
        width: Yup
          .number()
          .min(108, 'image.alimtalkItemHighlight.width.min'),
        height: Yup
          .number()
          .min(108, 'image.alimtalkItemHighlight.height.min'),
        ratio: Yup.number()
          .min(1, 'image.alimtalkItemHighlight.ratio.min')
          .max(1, 'image.alimtalkItemHighlight.ratio.max'),
      },
      'brandtalk-NORMAL_IMAGE': {
        size: Yup.number().max(5 * 1024 * 1024, 'brandImage.normal.size'),
        extension: Yup.string().allowExtension('brandImage.normal.extension'),
        width: Yup
          .number()
          .min(500, 'brandImage.normal.width.min')
          .max(4500, 'brandImage.normal.width.max'),
        height: Yup
          .number()
          .min(250, 'brandImage.normal.height.max')
          .max(4500, 'brandImage.normal.height.min'),
        ratio: Yup.number()
          .min(0.5, 'brandImage.normal.ratio.min')
          .max(4 / 3, 'brandImage.normal.ratio.max'),
      },
      'brandtalk-WIDE_IMAGE': {
        size: Yup.number().max(5 * 1024 * 1024, 'brandImage.normal.size'),
        extension: Yup.string().allowExtension('brandImage.normal.extension'),
        width: Yup
          .number()
          .min(500, 'brandImage.normal.width.min')
          .max(4500, 'brandImage.normal.width.max'),
        height: Yup
          .number()
          .min(250, 'brandImage.normal.height.max')
          .max(4500, 'brandImage.normal.height.min'),
        ratio: Yup.number()
          .min(0.5, 'brandImage.normal.ratio.min')
          .max(4 / 3, 'brandImage.normal.ratio.max'),
      },
      'brandtalk-MAIN_WIDE_ITEM_LIST_IMAGE': {
        size: Yup.number().max(5 * 1024 * 1024, 'brandImage.mainWideItemList.size'),
        extension: Yup.string().allowExtension('brandImage.mainWideItemList.extension'),
        width: Yup
          .number()
          .min(500, 'brandImage.mainWideItemList.width.min')
          .max(4500, 'brandImage.mainWideItemList.width.max'),
        height: Yup
          .number()
          .min(250, 'brandImage.mainWideItemList.height.min')
          .max(4500, 'brandImage.mainWideItemList.height.max'),
        ratio: Yup.number()
          .min(0.5, 'brandImage.mainWideItemList.ratio.min')
          .max(0.5, 'brandImage.mainWideItemList.ratio.max'),
      },
      'brandtalk-WIDE_ITEM_LIST_IMAGE': {
        size: Yup.number().max(5 * 1024 * 1024, 'brandImage.wideItemList.size'),
        extension: Yup.string().allowExtension('brandImage.wideItemList.extension'),
        width: Yup
          .number()
          .min(500, 'brandImage.wideItemList.width.min')
          .max(4500, 'brandImage.wideItemList.width.max'),
        height: Yup
          .number()
          .min(500, 'brandImage.wideItemList.height.min')
          .max(4500, 'brandImage.wideItemList.height.max'),
        ratio: Yup.number()
          .min(1, 'brandImage.wideItemList.ratio.min')
          .max(1, 'brandImage.wideItemList.ratio.max'),
      },
      'brandtalk-CAROUSEL_FEED_IMAGE': {
        size: Yup.number().max(5 * 1024 * 1024, 'brandImage.carouselFeed.size'),
        extension: Yup.string().allowExtension('brandImage.carouselFeed.extension'),
        width: Yup
          .number()
          .min(500, 'brandImage.carouselFeed.width.min')
          .max(4500, 'brandImage.carouselFeed.width.max'),
        height: Yup
          .number()
          .min(250, 'brandImage.carouselFeed.height.min')
          .max(4500, 'brandImage.carouselFeed.height.max'),
        ratio: Yup.number()
          .min(0.5, 'brandImage.carouselFeed.ratio.min')
          .max(4 / 3, 'brandImage.carouselFeed.ratio.max'),
      },
      'brandtalk-CAROUSEL_COMMERCE_IMAGE': {
        size: Yup.number().max(5 * 1024 * 1024, 'brandImage.carouselCommerce.size'),
        extension: Yup.string().allowExtension('brandImage.carouselCommerce.extension'),
        width: Yup
          .number()
          .min(500, 'brandImage.carouselCommerce.width.min')
          .max(4500, 'brandImage.carouselCommerce.width.max'),
        height: Yup
          .number()
          .min(250, 'brandImage.carouselCommerce.height.min')
          .max(4500, 'brandImage.carouselCommerce.height.max'),
        ratio: Yup.number()
          .min(0.5, 'brandImage.carouselCommerce.ratio.min')
          .max(4 / 3, 'brandImage.carouselCommerce.ratio.max'),
      },
    };

    return map[item.type];
  },
  serviceName: () => ({
    serviceName: Yup
      .string()
      .required('serviceName.required')
      .max(100, 'serviceName.max'),
  }),
  inquiryFile: (item) => {
    const result = {
      length: Yup
        .number()
        .max(10, 'inquiry.file.length.max'),
      list: item.list.map(() => ({
        size: Yup
          .number()
          .max(1 * 1024 * 1024, 'inquiry.file.size.max'),
        extension: Yup.string().allowInquiryFileExtension('inquiry.file.extension'),
      })),
      total: Yup
        .number()
        .max(5 * 1024 * 1024, 'inquiry.file.totalSize.max'),
    };

    return result;
  },
  inquiry: (item) => {
    const result = {
      content: Yup
        .string()
        .required('inquiry.content.required')
        .max(300, 'inquiry.content.max'),
      length: Yup
        .number()
        .max(10, 'inquiry.file.length.max'),
      list: item.list.map(() => ({
        size: Yup
          .number()
          .max(1 * 1024 * 1024, 'inquiry.file.size.max'),
        extension: Yup.string().allowInquiryFileExtension('inquiry.file.extension'),
      })),
      total: Yup
        .number()
        .max(5 * 1024 * 1024, 'inquiry.file.totalSize.max'),
    };

    return result;
  },
  rejectPhoneData: () => ({
    rejectPhoneId: Yup
      .string()
      .required('rejectPhone.id.required')
      .uuid('rejectPhone.id.uuid'),
    recipientNumber: Yup
      .string()
      .recipientNumber('rejectPhone.recipientNumber.recipientNumber')
      .required('rejectPhone.recipientNumber.required')
      .max(25, 'rejectPhone.recipientNumber.max'),
    comment: Yup
      .string()
      .max(1500, 'rejectPhone.comment.max'),
  }),
};

const mapToPromiseArray = ({
  data,
  map,
  results,
  labels,
  prefix = [],
}) => {
  Object.entries(map).forEach(([key, item]) => {
    // check object yup schema
    // eslint-disable-next-line no-underscore-dangle
    if (typeof item === 'object' && !item._whitelist) {
      mapToPromiseArray({
        data: data[key],
        map: item,
        results,
        labels,
        prefix: [...prefix, key],
      });
      return;
    }

    results.push(item.validate(data[key], data));
    labels.push([...prefix, key].join('.'));
  });
};

const validation = ({ data, type }) => {
  const results = [];
  const labels = [];

  mapToPromiseArray({
    data,
    map: validateMap[type](data),
    results,
    labels,
  });

  return [results, labels];
};

export default ({
  data,
  type,
  errors = {},
  form = {},
}) => {
  const error = errors;
  const element = form.value?.querySelector ? form.value : form.value?.$el;
  const { openAlertDialog } = useComponentStore();

  return new Promise((resolve, reject) => {
    const [promiseList, labels] = validation({ type, data });

    labels.forEach((label) => {
      error[label] = '';
    });

    Promise.allSettled(promiseList)
      .then((results) => {
        const rejected = [];

        results.forEach(({ status, reason }, index) => {
          if (status === 'rejected') {
            const label = labels[index];

            error[label] = reason.message;
            rejected.push(label);
          }
        });

        const item = rejected[0];

        if (item) {
          openAlertDialog({
            title: 'validation.title',
            content: `validation.${error[item]}`,
          });

          if (element) {
            element.querySelector(`[label="${item}"]`)?.focus?.();
          }

          reject(rejected);
          return;
        }

        resolve();
      })
      .catch(reject);
  });
};
